import React, { useState } from "react";
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Autocomplete,
  TextField,
} from "@mui/material";
import { BiSend } from "react-icons/bi";
import { addDiscussion } from "utils/followUpService";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import { IoCloseCircleOutline } from "react-icons/io5";
import useKothar from "context/useKothar";
import { sendNotification } from "utils/followUpService";
import UploadFile from "components/Input/UploadFile";

const inputClass =
  "w-full px-3 py-2 border border-app-border rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400 text-sm bg-app-surface";
const labelClass = "block text-xs font-medium text-app-text-secondary mb-1.5";

export default function FollowupDetailDialogs({
  notifyDialogOpen,
  setNotifyDialogOpen,
  addNoteDialogOpen,
  setAddNoteDialogOpen,
  followUpId,
  employeesLoading,
  employees,
  closeAddNoteDialog,
  selectedDiscussionId,
  setSelectedDiscussionId,
  refetchDiscussions,
}) {
  const [data, setData] = useState({
    text: "",
    attachments: null,
    nextFollowupDate: "",
    isInterested: "",
    interestedService: "",
    currentStatus: "",
    assignedCounsellor: "",
    happyWithService: "",
    badServiceReason: "",
    contactedDate: new Date().toISOString().split("T")[0],
  });

  const [{ usersList, isUsersListLoading }] = useKothar();
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [notifyMessage, setNotifyMessage] = useState("");
  const { mutate: addDiscussionMutation, isPending: isAddingDiscussion } = useMutation({
    mutationFn: (data) => addDiscussion(followUpId, data),
    onSuccess: () => {
      setAddNoteDialogOpen(false);
      toast.success("Note added successfully");
      refetchDiscussions();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.errorMessage || "Error adding note");
    },
  });

  const handleAddNoteSubmit = (e) => {
    e.preventDefault();
    addDiscussionMutation(data);
  };

  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const { mutate: sendNotificationMutation, isPending: isSendingNotification } = useMutation({
    mutationFn: (data) => sendNotification(followUpId, data),
    onSuccess: () => {
      toast.success("Notification sent successfully");
      setNotifyDialogOpen(false);
      refetchDiscussions();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.errorMessage || "Error sending notification");
    },
  });

  const handleSendNotification = () => {
    sendNotificationMutation({
      userId: selectedAdmin?.id,
      message: notifyMessage,
    });
  };

  return (
    <>
      <Dialog
        open={notifyDialogOpen}
        onClose={() => setNotifyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="font-semibold text-app-text text-base">
          Notify admin & counsellor
        </DialogTitle>
        <DialogContent className="pt-2">
          <p className="text-sm text-app-text-secondary mb-3">
            Send a notification about this discussion.
          </p>
          <Autocomplete
            options={usersList}
            getOptionLabel={(option) => option?.name}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by name..."
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "background.paper",
                    borderRadius: "10px",
                  },
                }}
              />
            )}
            value={selectedAdmin}
            onChange={(event, newValue) => setSelectedAdmin(newValue)}
          />
          <textarea
            value={notifyMessage}
            onChange={(e) => setNotifyMessage(e.target.value)}
            placeholder="Your message..."
            rows={4}
            className="mt-4 w-full px-3 py-2 border border-app-border rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
          />
        </DialogContent>
        <DialogActions className="gap-2 px-6 pb-5">
          <button
            onClick={() => setNotifyDialogOpen(false)}
            className="px-4 py-2 text-app-text-secondary hover:bg-app-surface-muted rounded-lg text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSendNotification}
            disabled={isSendingNotification || !notifyMessage.trim()}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            {isSendingNotification ? (
              <CircularProgress size={18} style={{ color: "white" }} />
            ) : null}
            Send notification
          </button>
        </DialogActions>
      </Dialog>

      <Dialog open={addNoteDialogOpen} onClose={closeAddNoteDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleAddNoteSubmit}>
          <DialogTitle className=" border-b-2 border-app-border rounded-t-lg">
            <div className="flex items-center justify-between px-4 py-2 font-semibold text-app-text text-base">
              Add note
              <IconButton onClick={closeAddNoteDialog}>
                <IoCloseCircleOutline className="text-2xl" />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent className="pt-2 space-y-4">
            <div className="p-2 space-y-4">
              <div>
                <label className={labelClass}>Note</label>
                <textarea
                  value={data.text}
                  onChange={handleChange}
                  placeholder="Enter note or update..."
                  rows={4}
                  name="text"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Attachments</label>
                <UploadFile data={data} setData={setData} imageKey="attachments" type="" />
              </div>
              <div>
                <label className={labelClass}>Next Followup Date</label>
                <input
                  type="date"
                  value={data.nextFollowupDate}
                  onChange={handleChange}
                  name="nextFollowupDate"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Interest</label>
                <select
                  value={data.isInterested}
                  onChange={handleChange}
                  name="isInterested"
                  className={inputClass}
                >
                  <option value="">Select</option>
                  <option value="Interested">Interested</option>
                  <option value="Not Interested">Not interested</option>
                  <option value="Maybe">Maybe</option>
                </select>
              </div>
              {data.isInterested === "Interested" && (
                <div>
                  <label className={labelClass}>Interested service</label>
                  <input
                    type="text"
                    value={data.interestedService}
                    onChange={handleChange}
                    name="interestedService"
                    placeholder="e.g. Student Visa"
                    className={inputClass}
                  />
                </div>
              )}
              <div>
                <label className={labelClass}>Current status</label>
                <input
                  type="text"
                  value={data.currentStatus}
                  onChange={handleChange}
                  name="currentStatus"
                  placeholder="e.g. Documents submitted"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Assigned counsellor</label>
                <select
                  value={data.assignedCounsellor}
                  onChange={handleChange}
                  name="assignedCounsellor"
                  disabled={employeesLoading}
                  className={inputClass + " disabled:bg-app-surface-muted"}
                >
                  <option value="">Select</option>
                  {employees?.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Contacted date</label>
                <input
                  type="date"
                  value={data.contactedDate}
                  onChange={handleChange}
                  name="contactedDate"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Happy with the service?</label>
                <select
                  value={data.happyWithService}
                  onChange={handleChange}
                  name="happyWithService"
                  className={inputClass}
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
                {data.happyWithService === "no" && (
                  <div className="mt-2">
                    <label className={labelClass}>Specify reason for bad service</label>
                    <textarea
                      value={data.badServiceReason}
                      onChange={handleChange}
                      name="badServiceReason"
                      placeholder="e.g. Delayed response, unclear communication..."
                      rows={3}
                      className={inputClass}
                    />
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
          <DialogActions className="gap-2 px-6 pb-5">
            <Button variant="outlined" color="primary" type="button" onClick={closeAddNoteDialog}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isAddingDiscussion || !data?.text?.trim()}
              startIcon={isAddingDiscussion ? <CircularProgress size={18} /> : <BiSend size={18} />}
            >
              Add note
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
